import type { PropsWithChildren } from 'react';
import { useEffect, createContext, useCallback, useState } from 'react';

import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import type { SignOutInput } from 'aws-amplify/auth';
import {
  signIn,
  signOut,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { sessionClearCurrentUserId } from '../state/actions';
import { useDispatch } from '../state/store';
import { COOKIE_IS_LOGGED_IN } from '../storage/cookieKeys';
import { clearCookie, getCookie, setCookie } from '../storage/cookies';

type HubPayload = {
  event: string;
  data?: unknown;
  message?: string;
};

export type HubCapsule = {
  channel: string;
  payload: HubPayload;
  source: string;
  patternInfo?: string[];
};

export enum LoginMfaOption {
  EMAIL,
  SMS,
}

export enum NextStep {
  NOTHING,
  MFA,
  RESET_PASSWORD,
}

export type MfaContext = {
  codeDeliveryDestination: string;
  codeExpiresAt: string;
  userName: string;
  isCustomMfa: boolean;
};

export type LoggedInUser = {
  uuid: string;
};

export class CustomMfaInvalidCodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CustomMfaInvalidCodeError';
  }
}

type AuthContextType = {
  installAuthInterceptor: (axiosClient: AxiosInstance) => AxiosInstance;
  logout: (options?: SignOutInput) => Promise<void>;
  login: (email: string, password: string, mfaOption?: LoginMfaOption) => Promise<NextStep>;
  confirmMfaCode: (confirmationCode: string) => Promise<void>;
  loginByCode: (userUuid: string, code: string) => Promise<NextStep>;
  isPrevPassword: (username: string, password: string) => Promise<boolean>;
  forgotPassword: (emailOrUsername: string) => Promise<void>;
  forgotPasswordSubmit: (username: string, code: string, newPassword: string) => Promise<void>;
  loginContext: { email: string; password: string; checkoutCode: string; userUuid: string };
  isLoggedIn?: boolean;
  loggedInUser: LoggedInUser | null | undefined;
  mfaContext: MfaContext | undefined;
};

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const AuthContextProvider = ({ children }: PropsWithChildren<unknown>) => {
  const queryClient = useQueryClient();
  const [mfaContext, setMfaContext] = useState<MfaContext | undefined>();

  const [loginContext, setLoginContext] = useState<{
    email: string;
    password: string;
    checkoutCode: string;
    userUuid: string;
  }>({
    email: '',
    password: '',
    checkoutCode: '',
    userUuid: '',
  });
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | undefined>();
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | null | undefined>(undefined);

  const dispatch = useDispatch();

  const loadAuthenticatedUser = async () => {
    console.log('Load authenticated user');

    try {
      const { tokens, userSub } = await fetchAuthSession();
      if (tokens !== undefined && userSub !== undefined) {
        setIsLoggedIn(true);
        setLoggedInUser({ uuid: userSub.toString() });
        setCookie(COOKIE_IS_LOGGED_IN, 'true');
        return;
      }
    } catch (err) {
      console.log('Error fetchAuthSession:', err);
    }

    setIsLoggedIn(false);
    setLoggedInUser(null);
  };

  const clearAuthContextData = useCallback(() => {
    console.log('Clearing auth context');

    setIsLoggedIn(false);
    setLoggedInUser(null); // Don't set to undefined.

    setMfaContext(undefined);
    setLoginContext({
      email: '',
      password: '',
      checkoutCode: '',
      userUuid: '',
    });

    clearCookie(COOKIE_IS_LOGGED_IN); // Clear cookie used to redirect user to logged in pages in SSR homepage.
    dispatch(sessionClearCurrentUserId()); // Clear redux state, remove once we move off of redux.

    queryClient.invalidateQueries(); // Clear react query cache.
  }, []);

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID_V2) {
      throw new Error('Environment variable NEXT_PUBLIC_COGNITO_CLIENT_ID_V2 is missing.');
    }

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: `${process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID}`,
          userPoolClientId: process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID_V2,
          userPoolEndpoint: process.env.NEXT_PUBLIC_COGNITO_URL ? process.env.NEXT_PUBLIC_COGNITO_URL : '',
        },
      },
    });

    const installHubListener = async () => {
      await loadAuthenticatedUser();

      Hub.listen('auth', data => {
        if (['signedOut', 'tokenRefresh_failure'].includes(data.payload.event)) {
          console.log(`Received auth event: ${data?.payload?.event}`);
          clearAuthContextData(); // Clear logged in user's data upon logout.
        } else if (['signedIn', 'tokenRefresh'].includes(data.payload.event)) {
          loadAuthenticatedUser();
        }
      });
    };

    installHubListener();

    return () => {
      Hub.listen('auth', () => console.log('Auth events unsubscribed'));
    };
  }, []);

  /**
   * Install Axios interceptor that injects auth token.
   *
   * @param client - Axios client instance.
   */
  const installAuthInterceptor = useCallback((client: AxiosInstance) => {
    // Use interceptor to inject the token to requests
    client.interceptors.request.use(
      async (cfg: InternalAxiosRequestConfig) => {
        try {
          const { accessToken } = (await fetchAuthSession()).tokens ?? {}; // Refreshes the token if the refresh token is still valid.
          const jwt = accessToken?.toString();
          if (jwt) {
            cfg.headers.Authorization = `Bearer ${jwt}`; // Add auth token to the header.
          } else {
            cfg.headers.Authorization = undefined;
          }
        } catch (e) {
          // noop. Logging error to console for debugging issues with calls to /deeplinks failing with 401s
          Sentry.captureException(e);
        }
        return cfg;
      },
      error => Promise.reject(error)
    );

    client.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        if (window && error?.response?.status === 503 && error?.response?.data?.errorCode === 666) {
          window.location.replace('/maintenance');
          return;
        } else if (error?.response?.status === 401) {
          console.log('Received a 401, signing out the user.');
          const testCookie = getCookie('testCookie');
          console.log('testCookie', testCookie);
          console.log('testItem', window.localStorage.getItem('testItem'));
          signOut();
          return;
        }
        return Promise.reject(error);
      }
    );

    return client;
  }, []);

  /**
   * @param email
   * @param password
   * @param mfaOption - The communication medium to deliver MFA code to user.
   */
  const login = async (email: string, password: string, mfaOption = LoginMfaOption.SMS) => {
    /**
     * We switch delivery medium of MFA code in Cognito's custom SMS sender lambda based on client ID.
     * This was done because we were not using Amplify client and it was difficult to implement the feature.
     *
     * TODO: Switch EMAIL MFA and previous password check to use Amplify's CUSTOM_AUTH flow,
     *       so we do not have to switch the client ids.
     */
    await queryClient.invalidateQueries();

    let nextStep = NextStep.NOTHING;
    if (mfaOption === LoginMfaOption.EMAIL) {
      try {
        await signIn({
          username: email,
          options: {
            authFlowType: 'CUSTOM_WITHOUT_SRP',
          },
        });
      } catch (err) {
        console.log({ err });
      }

      await confirmSignIn({
        challengeResponse: 'EMAIL_MFA_PASSWORD_STEP',
        options: { clientMetadata: { SELECTED_CHALLENGE: 'EMAIL_MFA_PASSWORD_STEP' } },
      });

      await confirmSignIn({
        challengeResponse: password,
      });

      setMfaContext({
        userName: '',
        codeExpiresAt: '',
        codeDeliveryDestination: email,
        isCustomMfa: true,
      });

      nextStep = NextStep.MFA;
    } else {
      const resp = await signIn({ username: email, password, options: { authFlowType: 'USER_SRP_AUTH' } });
      setMfaContext({
        userName: '',
        codeExpiresAt: '',
        codeDeliveryDestination: resp.nextStep.codeDeliveryDetails?.destination ?? '',
        isCustomMfa: false,
      });

      if (resp.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        nextStep = NextStep.MFA;
      } else if (resp.nextStep.signInStep === 'RESET_PASSWORD') {
        nextStep = NextStep.RESET_PASSWORD;
      }
    }

    // States used for MFA.
    setLoginContext({ email, password, checkoutCode: '', userUuid: '' });
    return nextStep;
  };

  /**
   * Password-less login, by checkout code or Capsule API key.
   *
   * @param userUuid - The uuid of user, "sub" field in JWT.
   * @param code - The checkout code or API key.
   */
  const loginByCode = useCallback(
    async (userUuid: string, code: string) => {
      await queryClient.invalidateQueries();

      const signInResp = await signIn({
        username: userUuid,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
        },
      });

      if (signInResp.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        await confirmSignIn({
          challengeResponse: 'API_KEY_CHALLENGE',
          options: { clientMetadata: { SELECTED_CHALLENGE: 'API_KEY_CHALLENGE' } },
        });

        const confirmResp = await confirmSignIn({
          challengeResponse: code,
        });

        if (confirmResp.nextStep.additionalInfo?.challengeType === 'CUSTOM_MFA') {
          setMfaContext({
            userName: confirmResp.nextStep.additionalInfo?.USERNAME || '',
            codeExpiresAt: confirmResp.nextStep.additionalInfo?.CODE_EXPIRES_AT || '',
            codeDeliveryDestination: confirmResp.nextStep.additionalInfo?.CODE_DELIVERY_DESTINATION || '',
            isCustomMfa: true,
          });
          setLoginContext({ email: '', password: '', checkoutCode: code, userUuid });
          return NextStep.MFA;
        } else {
          setMfaContext({
            userName: '',
            codeExpiresAt: '',
            codeDeliveryDestination: '',
            isCustomMfa: false,
          });
        }
      } else {
        throw new Error(`Invalid signInStep=${signInResp.nextStep.signInStep}`);
      }

      return NextStep.NOTHING;
    },
    [queryClient]
  );

  /**
   * Check to see if it matches the user's previous (current) password. Used to prevent
   * users from re-using their password during forgot password flow.
   *
   * @param username
   * @param password
   * @returns `false` if password matches the previous (current) password.
   */
  const isPrevPassword = async (username: string, password: string) => {
    await queryClient.invalidateQueries();

    await signOut(); // Make sure to sign out the existing user.

    await signIn({
      username: username,
      options: {
        authFlowType: 'CUSTOM_WITHOUT_SRP',
      },
    });

    await confirmSignIn({
      challengeResponse: 'PREV_PASSWORD_CHECK',
      options: { clientMetadata: { SELECTED_CHALLENGE: 'PREV_PASSWORD_CHECK' } },
    });

    try {
      await confirmSignIn({ challengeResponse: password });
    } catch (error) {
      return true;
    }
    return false;
  };

  const confirmMfaCode = async (confirmationCode: string) => {
    if (mfaContext?.isCustomMfa === true) {
      const resp = await confirmSignIn({ challengeResponse: confirmationCode });
      if (!resp.isSignedIn) {
        throw new CustomMfaInvalidCodeError('Invalid code.');
      }
    } else {
      await confirmSignIn({ challengeResponse: confirmationCode });
    }
  };

  const forgotPassword = async (emailOrUsername: string) => {
    await resetPassword({ username: emailOrUsername });
  };

  const forgotPasswordSubmit = async (username: string, code: string, newPassword: string) => {
    await confirmResetPassword({ username, confirmationCode: code, newPassword });
  };

  const logout = async (input?: SignOutInput) => {
    if (input) {
      await signOut(input);
    } else {
      await signOut();
    }
  };

  const values = {
    installAuthInterceptor,
    login,
    loginByCode,
    isPrevPassword,
    loginContext,
    isLoggedIn,
    mfaContext,
    loggedInUser,
    forgotPassword,
    forgotPasswordSubmit,
    logout,
    confirmMfaCode,
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
